'use client'

import Empty from '@/components/design-system/empty'
import type { Notification, NotificationType } from '@/types/notification'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import { Fragment, useMemo } from 'react'
import NotificationItem from '../notification-item'
import NotificationsLoadMore from '../notifications-load-more'
import NotificationsLoading from '../notifications-loading'
import NotificationsReadAllButton from '../notifications-read-all-button'
import useNotifications from '../use-notifications'
import useNotificationsCount from '../use-notifications-count'
import './notification-list.scss'

export default function NotificationList({ type }: { type: NotificationType }) {
  const t = useTranslations(`notifications.${type}`)

  const { notifications, isLoading, isLoadingMore, hasMore, loadMore } =
    useNotifications(type)

  const count = useNotificationsCount()

  const hasUnreadNotifications = count[type] > 0

  const groups = useMemo(() => {
    const today: Notification[] = []
    const yesterday: Notification[] = []
    const thisWeek: Notification[] = []
    const thisMonth: Notification[] = []
    const older: Notification[] = []

    notifications?.forEach((notification) => {
      const { createdAt } = notification

      const date = dayjs(createdAt)

      if (date.isAfter(dayjs().startOf('day'))) {
        today.push(notification)
      } else if (date.isAfter(dayjs().startOf('day').subtract(1, 'day'))) {
        yesterday.push(notification)
      } else if (date.isAfter(dayjs().startOf('week'))) {
        thisWeek.push(notification)
      } else if (date.isAfter(dayjs().startOf('month'))) {
        thisMonth.push(notification)
      } else {
        older.push(notification)
      }
    })

    return [
      { title: 'Today', items: today },
      { title: 'Yesterday', items: yesterday },
      { title: 'This week', items: thisWeek },
      { title: 'This month', items: thisMonth },
      { title: 'Older', items: older },
    ].filter(({ items }) => items.length > 0)
  }, [notifications])

  if (isLoading) {
    return <NotificationsLoading />
  }

  if (!notifications) {
    return null
  }

  if (notifications.length === 0) {
    return (
      <Empty
        title={t('no-results-title')}
        description={t('no-results-message')}
      />
    )
  }

  return (
    <>
      <ul className='notification-list'>
        {groups.map((group, index) => (
          <Fragment key={group.title}>
            <li className='notification-list__subheader'>
              {group.title}
              {index === 0 && hasUnreadNotifications && (
                <NotificationsReadAllButton type={type} />
              )}
            </li>
            {group.items.map((notification) => (
              <li key={notification.id} className='notification-list__item'>
                <NotificationItem type={type} notification={notification} />
              </li>
            ))}
          </Fragment>
        ))}
        {hasMore && !isLoading && (
          <NotificationsLoadMore
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMore}
          />
        )}
      </ul>
    </>
  )
}
